import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Provider, observer } from 'mobx-react';
import * as React from 'react';
import { useLocation } from 'react-router';
import { toast } from 'sonner';
import { getMaxAttachmentByteCountForEmailConfigurations } from '../../../../../../extViewmodels/Utils';
import {
	IContentCalendarSelectedSuggestion,
	ILocationState,
	IMultiCampaignSchedulerRequest,
} from '../../../../../../models';
import { CampaignType } from '../../../../../../models/AdminModels';
import { shouldShowDuplicateTemplateWarning } from '../../../../../../models/Campaigns';
import { EmailComposerContext, IEmailComposerContext } from '../../../../../../models/Email';
import { useEventLogging } from '../../../../../../models/Logging';
import { FirstNamePlaceholder, Token } from '../../../../../../models/Token';
import {
	applyDefaultExcludedTagsToContactsFilterRequest,
	convertRawRichTextContentStateToRichContentEditorState,
	getDefaultBulkMessagingBodyEditorState,
	getFileSizeStringValue,
	getUniqueIdForSuggestion,
	updateContactFilterSendFrom,
} from '../../../../../../models/UiUtils';
import { hasAnyBulkAiEligibleSuggestion } from '../../../../../../models/utils/contentCalendarBulkAiUtils';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useAccountProfileQuery, useCreateEmailMutation, useTemplateQueries } from '../../../../../../queries';
import { hasContentPreferences } from '../../../../../../utils/hasContentPreferences';
import { AdminUserSessionContext, ImpersonationContextViewModel } from '../../../../../../viewmodels/AdminViewModels';
import { ComposeEmailViewModel } from '../../../../../../viewmodels/AppViewModels';
import { AiButton } from '../../../../../components-v2/AiButton';
import { Button } from '../../../../../components-v2/Button';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../../components/MultiContainerHeader';
import { SendFromDropdown } from '../../../../../components/SendFromDropdown';
import { getTemplateApprovalStatus } from '../../../../../components/TemplateReviewStatusIndicator';
import { ComplianceApprovalPrompt } from '../../../../../components/campaigns/ComplianceApprovalPrompt';
import { EmailComposer } from '../../../../../components/email/EmailComposer';
import { ITestEmailResult, TestEmailModal } from '../../../../../components/email/TestEmailModal';
import { SendSchedulerComplete } from '../../../../../components/email/SendSchedulerComplete';
import { TagEmailRecipientsList } from '../../../../../components/email/TagEmailRecipientsList';
import { PaperPlaneIcon } from '../../../../../components/svgs/icons/PaperPlaneIcon';
import { ChevronLeft } from '../../../../../components/svgs/icons/ChevronLeft';
import { bs } from '../../../../../styles/styles';
import { CampaignComposerBaseLayout } from '../../../../BulkHwcPages/presentation';
import { useHandleAiGeneration } from '../../../hooks/handleAiGeneration';
import { WarningBanner, WarningBannerMessage } from '../../../presentation';
import { AccountSendFromDropdown } from '../../MultiCampaignSchedulerLayout';
import { MultiSchedulerAiModal } from '../../aiModal';
import { MultiEmailTemplateItem } from '../MultiEmailTemplateItem';
import { MultiEmailSchedulerSendOptions } from './presentation';
import { styleSheet } from './styles';
import { getEmailSubjectDisplay } from './utils';

interface Props {
	impersonationContext?: ImpersonationContextViewModel;
}

enum MultiEmailSchedulerStep {
	Recipients = 'recipients',
	ComplianceOptions = 'compliance-options',
	ScheduleSuccess = 'schedule-success',
}

function _MultiEmailScheduler({ impersonationContext }: Props) {
	const location = useLocation<ILocationState<any, IMultiCampaignSchedulerRequest>>();
	const request = location.state?.model;
	const onFinish = request?.onFinish;
	const isSequenceStep = request?.isSequenceStep;

	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession<AdminUserSessionContext>();
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });

	const { logApiError } = useEventLogging('MultiEmailScheduler');
	const [step, setStep] = React.useState(MultiEmailSchedulerStep.Recipients);
	const [activeCustomizationSuggestionId, setActiveCustomizationSuggestionId] = React.useState<string>(null);
	const [customEmails, setCustomEmails] = React.useState<
		Record<
			string,
			{ subject: string; content: Api.IRawRichTextContentState; attachments: File[]; aiReference?: Api.IAIReference }
		>
	>({});
	const [dateMap, setDateMap] = React.useState<Map<string, Date>>(() => new Map<string, Date>());
	const [selectedSuggestions, setSelectedSuggestions] = React.useState(request?.suggestions ?? []);

	const { pendingItems, queueItemForAiGeneration, isGenerating, processQueue, suggestionsWithErrors } =
		useHandleAiGeneration({
			contentType: 'email',
			impersonationContext,
			customContent: customEmails,
			onAiGenerationComplete: (
				suggestion: IContentCalendarSelectedSuggestion,
				toolCall: Api.EmailToolCall,
				conversationId: string
			) => {
				const key = getUniqueIdForSuggestion(suggestion);
				setCustomEmails(value => ({
					...value,
					[key]: {
						subject: toolCall.subject,
						content: toolCall.body,
						attachments: [],
						aiReference: { sessionId: conversationId },
					},
				}));
			},
		});
	const [showingOriginalContent, setShowingOriginalContent] = React.useState<string[]>([]);

	const activeCustomizationSuggestion = selectedSuggestions.find(
		s => getUniqueIdForSuggestion(s) === activeCustomizationSuggestionId
	);
	const [suggestionsMap] = React.useState<Map<string, IContentCalendarSelectedSuggestion>>(() => {
		const map = new Map<string, IContentCalendarSelectedSuggestion>();
		selectedSuggestions.forEach(suggestion => {
			const key = getUniqueIdForSuggestion(suggestion);
			map.set(key, suggestion);
		});
		return map;
	});

	const deselectSuggestion = React.useCallback(
		(suggestion: IContentCalendarSelectedSuggestion) => {
			setSelectedSuggestions(value => {
				const key = getUniqueIdForSuggestion(suggestion);
				const matchingSuggestion = suggestionsMap.get(key);
				if (matchingSuggestion) {
					suggestionsMap.delete(key);
					return value.filter(x => x !== matchingSuggestion);
				}
				return value;
			});
		},
		[suggestionsMap]
	);
	const getStartDateForSuggestion = React.useCallback(
		(suggestion: IContentCalendarSelectedSuggestion) => {
			const key = getUniqueIdForSuggestion(suggestion);
			const overrideDate = dateMap.get(key);
			return overrideDate
				? overrideDate
				: suggestion.schedule.startDate
					? new Date(suggestion.schedule.startDate)
					: null;
		},
		[dateMap]
	);
	const updateStartDateForSuggestion = React.useCallback(
		(suggestion: IContentCalendarSelectedSuggestion, date: Date) => {
			const key = getUniqueIdForSuggestion(suggestion);
			const matchingSuggestion = suggestionsMap.get(key);
			if (!matchingSuggestion) {
				return;
			}

			setDateMap(value => {
				value.set(key, date);
				return new Map(value);
			});
		},
		[suggestionsMap]
	);

	const requiresCompliance = impersonationContext
		? impersonationContext.account.preferences?.complianceSettings?.enabled
		: userSession.account?.preferences?.complianceSettings?.enabled;

	const getInitialSendFrom = () => {
		if (!impersonationContext?.user) {
			if (!impersonationContext?.account) {
				return Api.SendEmailFrom.CurrentUser;
			}
			return Api.SendEmailFrom.ContactOwner;
		}
		const currentUserId = userSession.user.id;
		const impersonatingUserId = impersonationContext.user.id;

		if (currentUserId === impersonatingUserId) {
			return Api.SendEmailFrom.CurrentUser;
		}

		return Api.SendEmailFrom.SelectedUser;
	};

	const [sendFrom, setSendFrom] = React.useState<Api.ISendEmailFrom>(getInitialSendFrom());
	const [sendFromUserId, setSendFromUserId] = React.useState<string | null>(null);

	const templateQueries = useTemplateQueries({
		impersonationContext: impersonationContext?.toJs(),
		templateIds: selectedSuggestions.map(t => t.templateReference.templateId),
	});

	const templates = templateQueries?.map(query => query.data) ?? [];
	const activeCustomizationTemplate = templates.find(
		t => t != null && t.id === activeCustomizationSuggestion?.templateReference.templateId
	);

	const [scheduledEmailResponses, setScheduledEmailResponses] = React.useState<Record<string, Api.ISendEmailResponse>>(
		{}
	);
	const suggestionEmailErrorsRef = React.useRef<Set<[IContentCalendarSelectedSuggestion, Api.IOperationResultNoValue]>>(
		new Set()
	);
	const createEmailMutation = useCreateEmailMutation();
	const unscheduledSuggestions =
		Object.keys(scheduledEmailResponses).length > 0
			? selectedSuggestions.filter(suggestion => {
					return scheduledEmailResponses[getUniqueIdForSuggestion(suggestion)] != null;
				})
			: selectedSuggestions;

	const [emailComposer, emailComposerContext] = React.useMemo((): [
		ComposeEmailViewModel<Api.IFollowUpOptions>,
		IEmailComposerContext<ComposeEmailViewModel<Api.IFollowUpOptions>>,
	] => {
		const sharedEmailComposer = new ComposeEmailViewModel(userSession).impersonate(impersonationContext);
		sharedEmailComposer.canCustomizeIndividualEmails = false;
		sharedEmailComposer.emailMessage = new Api.EmailMessageViewModel(userSession).impersonate(impersonationContext);
		sharedEmailComposer.emailMessage.contactsFilterRequest = {
			contactFilterRequest: applyDefaultExcludedTagsToContactsFilterRequest(sharedEmailComposer, {
				criteria: [],
			}),
			groupByDuplicate: true,
			ownershipFilter: {
				criteria: {
					property: sendFromUserId ? Api.ContactFilterCriteriaProperty.OwnedBy : Api.ContactFilterCriteriaProperty.All,
					value: sendFromUserId,
				},
			},
		};
		return [
			sharedEmailComposer,
			{
				emailComposer: sharedEmailComposer,
			},
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const sendTestEmailForScheduler = React.useCallback(
		async (
			selectedUserId: string,
			content: Api.IRawRichTextContentState,
			subject: string
		): Promise<Api.IOperationResult<Api.ISendEmailResponse>> => {
			if (!emailComposer?.emailMessage?.sendTestEmail) {
				throw new Error('sendTestEmail method not available');
			}
			const originalContent = emailComposer.emailMessage.content;
			const originalSubject = emailComposer.emailMessage.subject;
			try {
				emailComposer.emailMessage.content = content;
				emailComposer.emailMessage.subject = subject;
				return await emailComposer.emailMessage.sendTestEmail({
					to: { id: selectedUserId } as Api.ContactViewModel,
				});
			} finally {
				emailComposer.emailMessage.content = originalContent;
				emailComposer.emailMessage.subject = originalSubject;
			}
		},
		[emailComposer]
	);

	const partialCurrentUser = React.useMemo(() => {
		const { id, email, primaryEmail, firstName, lastName } = userSession.user;
		return {
			id,
			email,
			primaryEmail,
			firstName,
			lastName,
		};
	}, [userSession]);

	const onNextClicked = (_: React.MouseEvent<HTMLButtonElement>) => {
		if (requiresCompliance) {
			setStep(MultiEmailSchedulerStep.ComplianceOptions);
			return;
		}
		finalizeAndSchedule();
	};

	const [isScheduling, setIsScheduling] = React.useState(false);
	const finalizeAndSchedule = async (sendWithCompliance = false, complianceEmail?: string) => {
		const mainEmailMessage = emailComposer.emailMessage;
		const currentUser = userSession.user;

		suggestionEmailErrorsRef.current?.clear();
		if (!unscheduledSuggestions?.length) {
			return;
		}
		setIsScheduling(true);
		const results = await Promise.allSettled(
			unscheduledSuggestions.map(suggestion => {
				const matchingTemplate = templates.find(template => template.id === suggestion.templateReference.templateId);
				let subject = getEmailSubjectDisplay({
					customSubject: undefined, // No custom subject in this context
					templateSubject: matchingTemplate.subject,
					templateName: matchingTemplate.name,
					fallback: matchingTemplate.name, // Use name as fallback for sending
				});
				let content = matchingTemplate.content ?? getDefaultBulkMessagingBodyEditorState().getRawRichTextContent();
				const customEmail = customEmails[getUniqueIdForSuggestion(suggestion)];
				if (customEmail) {
					subject = customEmail.subject;
					content = customEmail.content;
				}

				const emailMessage = new Api.EmailMessageViewModel(userSession);
				emailMessage.setSavedAttachments(matchingTemplate?.attachments);
				emailMessage.templateReference = matchingTemplate
					? {
							isCustomized: Boolean(customEmail),
							isSystemTemplate: matchingTemplate.scope === Api.TemplateScope.Industry,
							templateId: matchingTemplate.id,
							name: matchingTemplate.name,
						}
					: null;
				emailMessage.subject = subject;
				emailMessage.content = content;
				emailMessage.aiReference = customEmail?.aiReference;
				emailMessage.contactsFilterRequest = mainEmailMessage.contactsFilterRequest;
				emailMessage.groupByHousehold = mainEmailMessage.groupByHousehold;
				emailMessage.contactsToAdd.addAll(mainEmailMessage.contactsToAdd.toArray());
				emailMessage.contactsToOmit.addAll(mainEmailMessage.contactsToOmit.toArray());

				const sendFromUser =
					sendFrom === Api.SendEmailFrom.CurrentUser
						? partialCurrentUser
						: sendFromUserId
							? { id: sendFromUserId }
							: null;

				const emailMessageModel = emailMessage.toJs();
				emailMessageModel.signatureTemplateId = 'default';
				emailMessageModel.options = {
					...emailMessageModel.options,
					scheduledSend: {
						expirationDate: matchingTemplate.schedule?.expirationDate,
						criteria: Api.ScheduleCriteria.StartAfter,
						startDate: getStartDateForSuggestion(suggestion).toISOString(),
					},
					sendWithCompliance,
					sendWithComplianceEmail: complianceEmail,
					sendEmailFrom: sendFrom,
					sendEmailFromUser: sendFromUser,
					sendEmailFromUserId: sendFrom === Api.SendEmailFrom.CurrentUser ? currentUser.id : sendFromUserId,
				};

				return createEmailMutation.mutateAsync({
					email: emailMessageModel,
					additionalAttachments: customEmail?.attachments,
					contentCalendarSuggestionId: suggestion.id,
					impersonationContext,
				});
			})
		);
		setIsScheduling(false);

		results.forEach((result, i) => {
			const suggestion = unscheduledSuggestions[i];
			if (result.status === 'fulfilled') {
				const response = result.value;
				setScheduledEmailResponses(value => ({
					...value,
					[getUniqueIdForSuggestion(suggestion)]: response,
				}));
			} else {
				const error = Api.asApiError(result.reason);
				logApiError('CreateEmail-Error', error);
				suggestionEmailErrorsRef.current.add([suggestion, error]);
			}
		});
		if (results.every(result => result.status === 'fulfilled')) {
			toast.success(`${selectedSuggestions.length} campaigns scheduled.`);
			if (isSequenceStep) {
				onFinish?.();
			} else {
				setStep(MultiEmailSchedulerStep.ScheduleSuccess);
				onFinish?.();
			}
		}
	};

	const onSendFromChange = (option: Api.ISendOnBehalfPermission, user?: Api.IUser) => {
		setSendFrom(option.sender.mode);
		setSendFromUserId(user?.id);
		impersonationContext?.update({
			account: impersonationContext?.account,
			user: user?.id ? ({ id: user.id } as Partial<Api.IUser>) : null,
		});
		updateContactFilterSendFrom({
			emailComposer,
			newSendFrom: option.sender.mode,
			newSendFromUserId: user?.id,
			partialCurrentUser,
			ownershipFilter: option.requiredFilter,
		});
	};
	const suggestionsWithoutDatesCount = selectedSuggestions.filter(s => !getStartDateForSuggestion(s)).length;

	const canSchedule = !suggestionsWithoutDatesCount && !isScheduling && !templateQueries.some(q => q.isPending);
	if (step === MultiEmailSchedulerStep.ScheduleSuccess) {
		return (
			<>
				<MultiContainerHeader
					fullscreenHeader='Schedule Campaigns'
					onFullscreenRequestBack={ev => {
						ev.preventDefault();
						fullscreenModal.dismissModal();
					}}
					onFullscreenRequestClose={() => fullscreenModal.dismissModal()}
				/>
				<SendSchedulerComplete styles={[styleSheet.emailSuccess]} />
			</>
		);
	}
	const exitCustomization = () => {
		setActiveCustomizationSuggestionId(null);
	};
	const handleSave = (customEmail: { subject: string; content: Api.IRawRichTextContentState; attachments: File[] }) => {
		setCustomEmails(value => ({
			...value,
			[activeCustomizationSuggestionId]: customEmail,
		}));
		exitCustomization();
	};
	return (
		<div className={css(styleSheet.container)}>
			<MultiContainerHeader
				fullscreenHeader='Schedule campaigns'
				onFullscreenRequestBack={ev => {
					ev.preventDefault();
					fullscreenModal.dismissModal();
				}}
				onFullscreenRequestClose={() => fullscreenModal.dismissModal()}
			/>
			{suggestionsWithoutDatesCount > 0 ? (
				<WarningBanner>
					<>
						<WarningBannerMessage>
							{`You have `}
							<strong>{suggestionsWithoutDatesCount}</strong>
							{` without a send date. Please select a date/time to continue scheduling these posts.`}
						</WarningBannerMessage>
					</>
				</WarningBanner>
			) : null}

			<CampaignComposerBaseLayout
				styles={[styleSheet.schedulerContainer]}
				mainHeader={
					<div className={css(styleSheet.headerContainer)}>
						<h3 className={css(styleSheet.headerTitle)}>Schedule campaigns</h3>
						{!activeCustomizationSuggestionId && userSession.canSendOnBehalf ? (
							<>
								{impersonationContext?.isValid ? (
									<AccountSendFromDropdown
										sendFromUserId={sendFromUserId}
										onSendFromChange={onSendFromChange}
										impersonationContext={impersonationContext}
									/>
								) : null}

								{!impersonationContext?.isValid ? (
									<SendFromDropdown
										allowedContactBasedOptions='all'
										sendOnBehalfOf={onSendFromChange}
										canSendOnBehalf
									/>
								) : null}
							</>
						) : null}
					</div>
				}
				sideBar={
					<Provider impersonationContext={impersonationContext}>
						{step === MultiEmailSchedulerStep.Recipients ? (
							<EmailComposerContext.Provider value={emailComposerContext}>
								<TagEmailRecipientsList
									canClearFilters={false}
									ctaButtonText={requiresCompliance ? 'Next: Compliance Options' : 'Schedule on suggested dates'}
									disableContactAdding={false}
									disableContactEdits={false}
									disableContactRemoval={false}
									disableCta={!canSchedule}
									disableOwnershipFilter={sendFrom === Api.SendEmailFrom.ContactOwner}
									emailComposer={emailComposer}
									key='recipients-list'
									onCtaClick={onNextClicked}
									styles={[styleSheet.recipientsList]}
									initialOwnershipFilter={emailComposer.emailMessage.contactsFilterRequest?.ownershipFilter}
								/>
							</EmailComposerContext.Provider>
						) : null}
						{step === MultiEmailSchedulerStep.ComplianceOptions ? (
							<ComplianceApprovalPrompt
								disabled={!canSchedule}
								isLoading={isScheduling}
								campaignType={CampaignType.Email}
								onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
									e.preventDefault();
									finalizeAndSchedule(true, sendWithComplianceEmail);
								}}
								onScheduleWithoutCompliance={() => finalizeAndSchedule(false)}
							/>
						) : null}
					</Provider>
				}
			>
				<>
					{hasContentPreferences(accountProfileQuery.data) && (
						<div className={css(styleSheet.customizeAllContentContainer)}>
							<AiButton
								title='Make Content Unique with AI'
								disabled={!hasAnyBulkAiEligibleSuggestion(selectedSuggestions)}
								onClick={() => {
									selectedSuggestions.forEach(suggestion =>
										queueItemForAiGeneration(suggestion, { forBulkAction: true })
									);
									processQueue();
								}}
								isLoading={isGenerating}
							/>
						</div>
					)}

					{activeCustomizationTemplate ? (
						<MultiEmailSchedulerCustomizeEmail
							impersonationContext={impersonationContext}
							onSave={handleSave}
							onCancel={exitCustomization}
							initialSubject={
								customEmails[activeCustomizationSuggestionId]
									? customEmails[activeCustomizationSuggestionId].subject
									: getEmailSubjectDisplay({
											customSubject: undefined,
											templateSubject: activeCustomizationTemplate.subject,
											templateName: activeCustomizationTemplate.name,
											fallback: activeCustomizationTemplate.name,
										})
							}
							initialContent={
								customEmails[activeCustomizationSuggestionId]
									? customEmails[activeCustomizationSuggestionId].content
									: activeCustomizationTemplate.content
							}
							savedAttachments={activeCustomizationTemplate.attachments}
							newAttachments={customEmails[activeCustomizationSuggestionId]?.attachments}
							sendTestEmail={sendTestEmailForScheduler}
						/>
					) : null}
					{!activeCustomizationTemplate ? (
						<>
							<div className={css(styleSheet.templatesList)}>
								{templateQueries.map((templateQuery, i) => {
									const suggestion = selectedSuggestions[i];
									const id = getUniqueIdForSuggestion(suggestion);
									if (!templateQuery.isSuccess) {
										return <LoadingSpinner key={`${id}-loading`} type='small' />;
									}
									const template = templateQuery.data;
									let customEmail = null;
									const shouldShowOriginalContent = showingOriginalContent.includes(id);

									if (!shouldShowOriginalContent) {
										customEmail = customEmails[id];
									}

									const generationFailed = suggestionsWithErrors.some(
										(s: IContentCalendarSelectedSuggestion) => id === getUniqueIdForSuggestion(s)
									);

									return (
										<MultiEmailTemplateItem
											key={id}
											scheduled={scheduledEmailResponses[id] != null}
											error={Array.from(suggestionEmailErrorsRef.current || []).find(([s]) => s === suggestion)?.[1]}
											name={
												<>
													{getEmailSubjectDisplay({
														customSubject: customEmail?.subject,
														templateSubject: template?.subject,
														templateName: template?.name,
													})}{' '}
													{getTemplateApprovalStatus(template?.templateReview)}
												</>
											}
											content={customEmail?.content ?? template?.content}
											scheduleDate={getStartDateForSuggestion(suggestion)}
											onUpdateDate={(newDate: Date) => updateStartDateForSuggestion(suggestion, newDate)}
											onRemove={templateQueries.length > 1 ? () => deselectSuggestion(suggestion) : undefined}
											sendFrom={sendFrom}
											sendFromUserId={sendFrom === Api.SendEmailFrom.CurrentUser ? userSession.user.id : sendFromUserId}
											postedOn={
												shouldShowDuplicateTemplateWarning(template) ? template?.lastUsedByDate?.account : undefined
											}
											impersonationContext={impersonationContext}
											onEdit={() => setActiveCustomizationSuggestionId(id)}
											isGeneratingContent={pendingItems.some(item => item.templateReference.templateId === template.id)}
											generationFailed={generationFailed}
											onGenerateContent={() => {
												queueItemForAiGeneration(suggestion);
												processQueue();
											}}
											showOriginalContent={shouldShowOriginalContent}
											onShowOriginalContent={
												customEmail || shouldShowOriginalContent
													? () => {
															if (showingOriginalContent.includes(id)) {
																setShowingOriginalContent(prev => prev.filter(showingId => showingId !== id));
															} else {
																setShowingOriginalContent(prev => [...prev, id]);
															}
														}
													: undefined
											}
										/>
									);
								})}
							</div>
							<MultiEmailSchedulerSendOptions
								emailComposer={emailComposer}
								impersonationContext={impersonationContext}
							/>
						</>
					) : null}
				</>
			</CampaignComposerBaseLayout>

			<MultiSchedulerAiModal
				hasEligibleBulkAiSuggestion={hasAnyBulkAiEligibleSuggestion(selectedSuggestions)}
				onGenerateContentClicked={() => {
					selectedSuggestions.forEach(suggestion => queueItemForAiGeneration(suggestion, { forBulkAction: true }));
					processQueue();
				}}
				impersonationContext={impersonationContext}
			/>
		</div>
	);
}
export const MultiEmailScheduler = observer(_MultiEmailScheduler);

export const MultiEmailSchedulerCustomizeEmail = ({
	impersonationContext,
	onSave,
	onCancel,
	initialSubject,
	initialContent,
	savedAttachments = [],
	newAttachments = [],
	sendTestEmail,
}: {
	impersonationContext: ImpersonationContextViewModel;
	onSave: ({
		subject,
		content,
		attachments,
	}: {
		subject: string;
		content: Api.IRawRichTextContentState;
		attachments: File[];
	}) => void;
	initialSubject: string;
	initialContent: Api.IRawRichTextContentState;
	savedAttachments?: Api.IFileAttachment[];
	newAttachments?: File[];
	onCancel: () => void;
	// Different signature from IEmailComposerContext.sendTestEmail: content and subject are passed
	// explicitly because this component manages its own editor state rather than using context.
	sendTestEmail?: (
		selectedUserId: string,
		content: Api.IRawRichTextContentState,
		subject: string
	) => Promise<Api.IOperationResult<Api.ISendEmailResponse>>;
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const attachmentByteSizeLimit = impersonationContext
		? getMaxAttachmentByteCountForEmailConfigurations(impersonationContext.account.emailProviderConfigurations)
		: getMaxAttachmentByteCountForEmailConfigurations(userSession.account.emailProviderConfiguration);
	const [attachmentsVM] = React.useState(
		new Api.AttachmentsToBeUploadedViewModel(newAttachments, attachmentByteSizeLimit)
	);
	const [subject, setSubject] = React.useState(initialSubject);
	const [emailBodyEditorState, setEmailBodyEditorState] = React.useState(
		initialContent
			? convertRawRichTextContentStateToRichContentEditorState(initialContent)
			: getDefaultBulkMessagingBodyEditorState()
	);
	const [messageFieldError, setMessageFieldError] = React.useState<string>();
	const [subjectFieldError, setSubjectFieldError] = React.useState<string>();
	const onMessageEditorStateChanged = (editorState: Api.IRichContentEditorState) => {
		setEmailBodyEditorState(editorState);
		setMessageFieldError('');
		setSubjectFieldError('');
	};
	const clearErrorMessages = () => {
		setMessageFieldError('');
		setSubjectFieldError('');
	};
	const onSubjectChanged = (value: string) => {
		setSubject(value);
		clearErrorMessages();
	};
	const [showingTestEmailModal, setShowingTestEmailModal] = React.useState(false);
	const onTestEmailModalClose = (result?: ITestEmailResult, canceled?: boolean) => {
		setShowingTestEmailModal(false);
		if (result && result.success && !canceled) {
			toast.success('Test email sent successfully!');
		}
	};
	// useMemo re-creates the wrapper whenever content or subject changes so TestEmailModal always
	// sends the current editor state. eslint-disable: getRawRichTextContent() is called inside
	// the factory, not as a dependency — the editorState object reference is the dependency.
	const sendTestEmailWithContent = React.useMemo(
		() =>
			sendTestEmail
				? (userId: string) => sendTestEmail(userId, emailBodyEditorState.getRawRichTextContent(), subject)
				: undefined,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sendTestEmail, emailBodyEditorState, subject]
	);
	const handleSave = () => {
		if (!subject) {
			setMessageFieldError('');
			setSubjectFieldError('Please enter a subject before saving.');
			return;
		}
		if (!emailBodyEditorState || (!!emailBodyEditorState && !emailBodyEditorState.hasContent())) {
			setMessageFieldError('Please enter a message before saving.');
			setSubjectFieldError('');
			return;
		}
		const newContent = emailBodyEditorState.getRawRichTextContent();
		if (!Token.isFirstNameTokenFormattedCorrectlyLoose(newContent)) {
			errorMessages.push({
				messages: [
					`The first name token does not appear to be formatted properly. It should be ${FirstNamePlaceholder.symbol}.`,
				],
			});
			return;
		}
		const hasExceededMaxByteCount =
			attachmentsVM.byteCount + savedAttachments.reduce((count, a) => count + a.fileSize, 0) > attachmentByteSizeLimit;
		if (hasExceededMaxByteCount) {
			setMessageFieldError(
				`You've exceeded the attachment size limit of ${getFileSizeStringValue(attachmentByteSizeLimit)}`
			);
			setSubjectFieldError('');
			return;
		}
		onSave({ subject, content: emailBodyEditorState.getRawRichTextContent(), attachments: attachmentsVM.attachments });
	};
	return (
		<div className={css(styleSheet.customizeEmailContainer)}>
			{!!sendTestEmailWithContent && (
				<TestEmailModal
					isOpen={showingTestEmailModal}
					onRequestClose={onTestEmailModalClose}
					onSendTestEmail={sendTestEmailWithContent}
				/>
			)}
			<div className={css(bs.flex, bs.itemsCenter, bs.justifyBetween)}>
				<button onClick={onCancel} className={css(styleSheet.backButton)}>
					<ChevronLeft /> Back to templates
				</button>
				{!!sendTestEmailWithContent && (
					<Button variant='link' onClick={() => setShowingTestEmailModal(true)}>
						<PaperPlaneIcon />
						Send a Test
					</Button>
				)}
			</div>
			<EmailComposer
				className={css(styleSheet.bodyEditor)}
				attachments={attachmentsVM}
				bodyEditorState={emailBodyEditorState}
				bodyError={messageFieldError}
				hideBodyFooterToolbar
				hideBodyTemplateSelectorButton
				onBodyEditorFocusChanged={clearErrorMessages}
				onBodyEditorStateChanged={onMessageEditorStateChanged}
				onBodyFieldScroll={clearErrorMessages}
				onSubjectChanged={onSubjectChanged}
				savedAttachments={savedAttachments}
				subject={subject}
				subjectError={subjectFieldError}
			/>
			<div className={css(bs.flex, bs.justifyEnd, bs.itemsCenter, bs.gap2)}>
				<Button onClick={handleSave}>Save</Button>
				<Button variant='secondary' onClick={onCancel}>
					Cancel
				</Button>
			</div>
		</div>
	);
};
